import { Button as ButtonPrimitive } from "@base-ui/react/button";
import type { VariantProps } from "class-variance-authority";
import { Children, isValidElement, type ReactNode } from "react";

import { buttonVariants } from "#components/button-variants";
import { Tip } from "#components/tooltip";
import { cn } from "#lib/utils";

function Button({
  className,
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & { loading?: boolean }) {
  const [lead, ...rest] = Children.toArray(children);
  const control = (
    <ButtonPrimitive
      data-slot="button"
      data-variant={variant}
      data-size={size}
      disabled={disabled || loading}
      focusableWhenDisabled={loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {!loading ? (
        children
      ) : isValidElement(lead) && rest.length > 0 ? (
        <>
          <Spinning>{lead}</Spinning>
          {rest}
        </>
      ) : (
        <Spinning>{children}</Spinning>
      )}
    </ButtonPrimitive>
  );
  const name = props["aria-label"];
  // An icon size has no visible label, so its name shows on hover.
  if (!name || (size !== "icon" && size !== "icon-sm")) return control;
  return <Tip label={name}>{control}</Tip>;
}

// Founder, 30 Oct 2026: a pending button keeps its width. The spinner covers the leading icon or
// the label, which stays laid out at opacity 0 (`invisible` would drop it from the name).
function Spinning({ children }: { readonly children: ReactNode }) {
  return (
    <span className="relative inline-flex items-center justify-center gap-[inherit]">
      <span className="inline-flex items-center gap-[inherit] opacity-0">
        {children}
      </span>
      {/* Drawn in CSS: the marketing site renders these classes on the server, without icons. */}
      <span
        aria-hidden="true"
        data-slot="spinner"
        className="absolute top-1/2 left-1/2 size-[var(--icon-size)] -translate-1/2 animate-spin rounded-full border-2 border-current border-r-transparent"
      />
    </span>
  );
}

export { Button };
